/**
 * What both remote-pose transports share (remote-pose-runtime.js over the service's own WebRTC,
 * livekit-pose-runtime.js over LiveKit): the video stream to send, the encoder cap, and the
 * `model-result` / `heartbeat` messages turned into what use-predict-worker reads from `detect()`.
 */

import { useCameraManagerStore } from '../services/camera/store';
import { onServiceMessage } from './pose-latency-probe';

// Defaults for what the sender encodes; the store passes the environment's values
// (config/features.js POSE_STREAM_WIDTH / POSE_STREAM_FPS). Measured 2026-10-20
// (backend/pose_service/docs/stream_specs_2026-10-20.md): the body model sees a 512 px crop of the
// person, so 480 px wide counts as 640; the matcher takes 15 fps of whatever arrives.
export const TARGET_WIDTH = 640;
export const TARGET_FPS = 15;
const STREAM_WAIT_MS = 30000;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export function liveStream(stream) {
  const live = stream?.getVideoTracks?.().some(track => track.readyState === 'live');
  return live ? stream : null;
}

export function storeStream() {
  try {
    return liveStream(useCameraManagerStore.getState().runtime?.stream);
  } catch (error) {
    return null;
  }
}

export function elementStream(videoElement) {
  if (!videoElement) {
    return null;
  }
  const attached = liveStream(videoElement.srcObject);
  if (attached) {
    return attached;
  }
  const capture = videoElement.captureStream || videoElement.mozCaptureStream;
  return capture ? liveStream(capture.call(videoElement)) : null;
}

/**
 * The video stream to send: one attached by the hook, else the camera store's. The runtime never opens
 * the camera itself: the exercise page builds it only once the camera is live.
 */
export async function waitForStream(state, label) {
  const waitStart = performance.now();
  for (;;) {
    const stream = state.attachedStream || storeStream();
    if (stream) {
      return stream;
    }
    if (performance.now() - waitStart > STREAM_WAIT_MS) {
      throw new Error(`${label}: no live video stream to send`);
    }
    await sleep(100);
  }
}

// Cap what is encoded, not the camera: scaling in the encoder leaves the camera track (and the
// patient's mirror) at its own size and rate, where a track constraint would shrink the preview and
// on some phones re-open the camera. Only once the sender is negotiated does it have encodings.
export async function capWidth(sender, target, fps = TARGET_FPS) {
  const width = sender.track?.getSettings?.().width || 0;
  const params = sender.getParameters();
  if (!params.encodings || !params.encodings.length) {
    params.encodings = [{}];
  }
  params.encodings[0].scaleResolutionDownBy = width > target ? width / target : 1;
  params.encodings[0].maxFramerate = fps;
  try {
    await sender.setParameters(params);
  } catch (error) {
    console.warn('[remote-pose] encoder cap failed', error);
  }
}

/** One message from the pose service: the newest `model-result` is kept, a heartbeat counted. */
export function readResultMessage(state, text) {
  try {
    const message = JSON.parse(text);
    if (message.type === 'model-result') {
      state.latest = message;
      state.unread = true;
      state.results += 1;
      state.lastResultAt = performance.now();
      state.lastResultWallMs = Date.now();
    } else if (message.type === 'heartbeat') {
      state.heartbeats += 1;
    }
    onServiceMessage(message);
  } catch (error) {
    console.warn('[remote-pose] bad message', error);
  }
}

function toDetectResult(result, receivedAt, receivedWallMs) {
  const person = result.persons?.[0];
  const landmarks = person?.mediapipe_keypoints
    ? [person.mediapipe_keypoints.map(({ x, y, z, visibility }) => ({ x, y, z, visibility }))]
    : [];
  return {
    durationMs: result.latency_ms ?? 0,
    landmarks,
    worldLandmarks: [],
    mhr: person?.mhr ?? null,
    timing: null,
    remote: {
      fps: result.fps,
      latencyMs: result.latency_ms,
      avgLatencyMs: result.avg_latency_ms,
      framesProcessed: result.frames_processed,
      frameW: result.frame_w,
      frameH: result.frame_h,
      // Service wall clock at send time: on one machine the transport age of the result.
      resultAgeMs: Math.round(Date.now() - (result.timestamp_ms ?? Date.now())),
      receivedAt,
      // The pose's own clock: the frame's presentation time at the service (seconds since its first
      // frame) and the service's send time (ms, wall clock). The hook relays `frameT` to the backend as
      // `poseTime`, so the rotation matcher times its tokens by the pose, not by the tick that carried it.
      frameT: typeof result.frame_t === 'number' ? result.frame_t : null,
      timestampMs: typeof result.timestamp_ms === 'number' ? result.timestamp_ms : null,
      // LiveKit only: the frame's capture on this device's wall clock and its time on the pod
      // (pose-latency-probe.js).
      captureMs: typeof result.capture_ms === 'number' ? result.capture_ms : null,
      podMs: typeof result.pod_ms === 'number' ? result.pod_ms : null,
      receivedWallMs,
    },
  };
}

/** `detect()`: the newest unread result, or `{ dropped: true }` when nothing new arrived. */
export function takeResult(state) {
  if (!state.unread || !state.latest) {
    return { dropped: true };
  }
  state.unread = false;
  return toDetectResult(state.latest, state.lastResultAt, state.lastResultWallMs);
}
